/**
 * Сводка вкладки «Голоса клиента» слоями (#2060, спека
 * 2026-10-11-voice-report-layers.md §2.1) — без React.
 *
 * «Главное» в карточке — строка с наибольшим числом разговоров (решение
 * владельца В3), а не с самой большой долей: карточка, названная по доле,
 * вела бы на малую выборку. Кандидат — строка не меньше порога малой выборки
 * (`calls ≥ порог`, а не `!isSmallSample`: тот ложен и для нуля), и не сборный
 * код (`other`, «Без темы») — «главное — Другое» ничего не называет.
 * Кандидатов нет — «пока мало данных». Порядок строк в блоках не меняется.
 *
 * Сдвиг карточки — тот же, что у строки той же сущности: иначе экран показал
 * бы стрелку там, где строка её не показывает.
 */
import type { TFunction } from 'i18next';
import {
  VOICE_SMALL_SAMPLE_CALLS,
  type ObjectionStreamResponse,
  type VoiceCustomerQuestions,
  type VoiceThemes,
} from '@aica/api-types/schemas';
import { localizeObjectionCode } from '@/components/dashboards/objectionCodeNames';
import {
  markedObjectionTrend,
  orderCodes,
  type ObjectionTrend,
} from '@/components/analytics/objections/objection-view-model';
import {
  declineSection,
  firstMinuteSection,
  questionSection,
  unansweredSection,
} from './voice-questions-view-model';
import {
  objectionSeriesRef,
  rowSeriesRef,
  type VoiceSeriesRef,
} from './voice-series-view-model';
import { themeSection } from './voice-themes-view-model';
import { rowLabel, rowMark, type VoiceMark, type VoiceRow } from './voice-view-model';

/** The screen's `t`: objection names are translated with i18next's own signature. */
type Translate = TFunction;

/** Сборный код словарей возражений, вопросов и отказов. */
const CATCH_ALL_CODE = 'other';

/** Что карточка называет главным. */
export interface SummaryLead {
  name: string;
  calls: number;
  /**
   * Сдвиг строки той же сущности: у возражения — его правило, у строк
   * «Голоса клиента» — пометка Р3 с сервера; у вопросов без ответа сдвига нет.
   */
  trend: ObjectionTrend | VoiceMark;
  /** Подсказка к сдвигу: доля прошлого периода, словами строки. */
  prevShareKey?: string;
  /** Знаменатель словами. */
  basis: string;
  /**
   * Ряд той же строки (#2553) — спарклайн карточки, из ответа ряда её блока;
   * нет — у счёта карточки ряда нет (без ответа, первая минута).
   */
  seriesRef?: VoiceSeriesRef | null;
}

/** `null` — кандидатов нет: «пока мало данных». */
export type SummaryLeadResult = SummaryLead | null;

/** Что карточке известно: ответ ещё идёт, не пришёл или пришёл. */
export type SummaryCardState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | {
      kind: 'ready';
      lead: SummaryLeadResult;
      /**
       * The count's answer as the cache holds it — a new object only when its
       * contents changed (structural sharing): the series of the card's scope
       * go out of date with it (#2712).
       */
      answer: unknown;
    };

/** Что карточка читает у запроса вкладки. */
export interface SummaryCardQuery<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  /**
   * The previous filter's answer kept while the new one loads: the card names
   * no lead of the old filter under the new one.
   */
  isPlaceholderData?: boolean;
  /** The answer `data` is derived from, when it is (themes); else `data` itself. */
  answer?: unknown;
}

/** Состояние карточки по запросу вкладки и правилу «главного». */
export function summaryCardState<T>(
  query: SummaryCardQuery<T>,
  lead: (data: T) => SummaryLeadResult | undefined
): SummaryCardState {
  if (query.isPending || query.isPlaceholderData) return { kind: 'loading' };
  // A failed refetch keeps the previous answer: it is not named as current,
  // as the section under the card says it could not load.
  if (query.isError) return { kind: 'failed' };
  const result = query.data === undefined ? undefined : lead(query.data);
  return result === undefined
    ? { kind: 'failed' }
    : { kind: 'ready', lead: result, answer: query.answer ?? query.data };
}

/** Строка с наибольшим числом разговоров среди кандидатов; при равенстве — первая. */
export function leadRow<T>(
  rows: readonly T[],
  calls: (row: T) => number,
  catchAll: (row: T) => boolean
): T | undefined {
  let lead: T | undefined;
  for (const row of rows) {
    if (calls(row) < VOICE_SMALL_SAMPLE_CALLS || catchAll(row)) continue;
    if (lead === undefined || calls(row) > calls(lead)) lead = row;
  }
  return lead;
}

const rowIsCatchAll = (row: VoiceRow) => row.key === CATCH_ALL_CODE;

function rowLead(
  row: VoiceRow | undefined,
  lead: Omit<SummaryLead, 'name' | 'calls'>,
  t: Translate
) {
  return row
    ? { name: rowLabel(row, t), calls: row.calls, seriesRef: rowSeriesRef(row), ...lead }
    : null;
}

/** «Главное возражение» — по взгляду «Рынок», как его первый код. */
export function objectionLead(
  data: ObjectionStreamResponse,
  t: Translate
): SummaryLeadResult {
  const code = leadRow(
    orderCodes(data.codes, 'market'),
    (row) => row.calls,
    (row) => row.code === CATCH_ALL_CODE
  );
  if (!code) return null;
  return {
    name: localizeObjectionCode(code.code, code.wordings[0] ?? code.code, t),
    calls: code.calls,
    // The tab asks the rule Р3 marks: «new» is the server's (#2698).
    trend: markedObjectionTrend(code, {
      recognizedCalls: data.recognizedCalls,
      prevRecognizedCalls: data.prevRecognizedCalls,
      prevHasUnrecognized: data.prevHasUnrecognized,
      prevTruncated: data.prevTruncated,
    }),
    prevShareKey: 'objectionsPage.trend.prevShare',
    basis: t('voicePage.layers.summary.objectionBasis', { count: data.recognizedCalls }),
    // The code's own series, as its row asks it (`ObjectionsList`).
    seriesRef: objectionSeriesRef(code.code),
  };
}

/** «Главная причина отказа» — строка «Почему отказываются». */
export function declineLead(cq: VoiceCustomerQuestions, t: Translate): SummaryLeadResult {
  const row = leadRow(declineSection(cq).rows, (r) => r.calls, rowIsCatchAll);
  return rowLead(
    row,
    {
      trend: row ? rowMark(row) : { kind: 'none' },
      prevShareKey: 'voicePage.trend.prevShare',
      basis: t('voicePage.layers.summary.declineBasis', {
        count: cq.declines.callsInWindow,
      }),
    },
    t
  );
}

/**
 * «Главный вопрос без ответа» — по числу разговоров без полного ответа, не по
 * доле. Прошлого окна у этого счёта в ответе нет — карточка без сдвига, как и
 * строка блока.
 */
export function unansweredLead(
  cq: VoiceCustomerQuestions,
  t: Translate
): SummaryLeadResult {
  const row = leadRow(unansweredSection(cq).rows, (r) => r.calls, rowIsCatchAll);
  return rowLead(
    row,
    {
      trend: { kind: 'none' },
      basis: t('voicePage.layers.summary.unansweredBasis', {
        count: row?.callsInWindow ?? 0,
      }),
    },
    t
  );
}

/**
 * «Главная тема» — строка блока тем, «Без темы» не кандидат (она не в строках).
 * Под фильтром темы (#2229, §2.5) — строка выбранной темы: блок тем фильтром
 * не сужен, и карточка называет её «K из N разговоров с темами».
 */
export function themeLead(
  themes: VoiceThemes,
  t: Translate,
  selected?: string
): SummaryLeadResult {
  const rows = themeSection(themes).rows;
  const row =
    selected === undefined
      ? leadRow(
          rows,
          (r) => r.calls,
          () => false
        )
      : rows.find((r) => r.id === `theme:${selected}`);
  return rowLead(
    row,
    {
      // A theme whose previous window is not comparable has no previous numbers:
      // the row names its base instead of an arrow, and so does the card.
      trend: row && !row.theme?.baseline ? rowMark(row) : { kind: 'none' },
      prevShareKey: 'voicePage.trend.prevShare',
      basis: t('voicePage.layers.summary.themeBasis', { count: row?.callsInWindow ?? 0 }),
    },
    t
  );
}

/** «Чаще всего спрашивают» — строка «Частых вопросов по предмету» («Сайт и контент»). */
export function frequentLead(
  cq: VoiceCustomerQuestions,
  t: Translate
): SummaryLeadResult {
  const row = leadRow(questionSection(cq).rows, (r) => r.calls, rowIsCatchAll);
  return rowLead(
    row,
    {
      trend: row ? rowMark(row) : { kind: 'none' },
      prevShareKey: 'voicePage.trend.prevShare',
      basis: t('voicePage.layers.summary.frequentBasis', {
        count: cq.questions.callsInWindow,
      }),
    },
    t
  );
}

/** «В первую минуту» — строка «Вопросов первой минуты»: звонки, а с #1578 и чаты. */
export function firstMinuteLead(
  cq: VoiceCustomerQuestions,
  t: Translate
): SummaryLeadResult {
  const row = leadRow(firstMinuteSection(cq).rows, (r) => r.calls, rowIsCatchAll);
  return rowLead(
    row,
    {
      trend: row ? rowMark(row) : { kind: 'none' },
      prevShareKey: 'voicePage.trend.prevShare',
      basis: t(
        cq.firstMinute.includesChats === true
          ? 'voicePage.layers.summary.firstMinuteBasisWithChats'
          : 'voicePage.layers.summary.firstMinuteBasis',
        { count: cq.firstMinute.callsInWindow }
      ),
    },
    t
  );
}

/**
 * «Тема с вопросами без ответа» («Сайт и контент», срез `questions`): тема, в
 * разговорах которой больше всего вопросов осталось без полного ответа. Прошлого
 * окна у этого счёта нет — карточка без сдвига, как строка вопросов без ответа.
 */
export function themeUnansweredLead(
  themes: VoiceThemes,
  t: Translate,
  /** The theme filter's theme (#2229): its own row, whatever the others hold. */
  selected?: string
): SummaryLeadResult {
  const row =
    selected === undefined
      ? leadRow(
          themes.rows,
          (r) => r.unanswered?.calls ?? 0,
          () => false
        )
      : themes.rows.find((r) => r.code === selected);
  if (!row?.unanswered) return null;
  return {
    name: row.name,
    calls: row.unanswered.calls,
    trend: { kind: 'none' },
    basis: t('voicePage.layers.summary.themeUnansweredBasis', { count: row.calls }),
  };
}
